// B390 (AI_157) — CONDUTAS SUGERIDAS PELA ÚLTIMA ANÁLISE GERAL.
//
// Pedido de Leonardo (02/11): as sugestões de conduta da análise "ficam
// perdidas" — ao atender de novo, ele esquece de conferir. Este módulo
// transforma a seção "4. RECOMENDAÇÕES" do texto da análise numa lista
// conferível (quadro no topo da aba Consultas), com estado por item.
//
// Fatos que mandam no desenho:
//   • a análise é UM texto (resumo.principaisAchados[0]); o campo estruturado
//     `sugestoes` está vazio desde sempre → extrair do texto;
//   • extração DETERMINÍSTICA, sem IA, custo zero; se o formato derivar e a
//     seção não for achada, devolve [] (o quadro avisa — nunca inventa);
//   • a dica "provável já prescrito" é SÓ dica (cruzamento por nome de
//     medicamento/exame em receitas e pedidos POSTERIORES à análise): o
//     cruzamento erra nos dois sentidos ("reduzir losartana" casa com receita
//     que mantém losartana) → o médico confirma com um clique, nada apaga só.
// Puro: sem React, sem Firestore. Testes em condutasSugeridas.test.ts.

export type SinalConduta = "verde" | "amarelo" | "vermelho" | null;

export interface ItemConduta {
  /** Chave estável (hash do texto normalizado) — é o nome do campo no Firestore. */
  chave: string;
  grupo: string;
  sinal: SinalConduta;
  texto: string;
  detalhes: string[];
}

export interface GrupoConduta {
  titulo: string;
  itens: ItemConduta[];
}

export interface EstadoConduta {
  status: "acatada" | "descartada";
  em: string; // ISO
  porUid: string;
  porEmail: string | null;
  motivo?: string;
}

const EMOJIS = /[🟢🟡🔴🟠]/u;

const normalizar = (s: string): string =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Tira marcadores de lista/negrito/número/emoji do começo da linha. */
function limparMarcadores(l: string): string {
  return l
    .replace(/^\s*(?:[-•*]\s+)?/, "")
    .replace(/^\s*\d{1,2}[.)]\s*/, "")
    .replace(/\*\*/g, "")
    .replace(/[🟢🟡🔴🟠]\s*/gu, "")
    .trim();
}

function sinalDe(l: string): SinalConduta {
  if (/🔴/u.test(l)) return "vermelho";
  if (/🟠|🟡/u.test(l)) return "amarelo";
  if (/🟢/u.test(l)) return "verde";
  return null;
}

/** Título de seção em CAIXA ALTA (espelha ehTituloSecao da AbaAnaliseIA). */
function ehTituloCaps(l: string): boolean {
  const t = l
    .replace(/^\s*#+\s*/, "")
    .replace(/\*\*/g, "")
    .replace(/^\d{1,2}[.)]\s*/, "")
    .trim();
  return t.length >= 3 && t.length <= 60 && /[A-ZÀ-Ý]/.test(t) && !/[a-zà-ÿ]/.test(t);
}

function ehInicioRecomendacoes(l: string): boolean {
  return ehTituloCaps(l) && /RECOMENDA/.test(normalizar(l).toUpperCase());
}

/** Fim da seção: próximo título CAPS NUMERADO ou "CONSIDERAÇÕES". */
function ehFimRecomendacoes(l: string): boolean {
  if (!ehTituloCaps(l)) return false;
  const t = l.replace(/^\s*#+\s*/, "").replace(/\*\*/g, "").trim();
  return /^\d{1,2}[.)]\s*\S/.test(t) || /CONSIDERA/.test(normalizar(t).toUpperCase());
}

const NOMES_DE_GRUPO =
  /^(condutas?|ajustes?|exames?|candidatura|monitoramento|seguimento|encaminhamentos?|vacina|medica|imagem|laborat|orienta|pend)/i;

function ehSubtitulo(l: string): boolean {
  const bruto = l.trim();
  if (!bruto || EMOJIS.test(bruto)) return false;
  if (/^\s*(?:[-•*]\s+|\d{1,2}[.)]\s+)/.test(bruto)) return false;
  const t = bruto.replace(/^#+\s*/, "").replace(/\*\*/g, "").trim();
  if (t.length > 60) return false;
  return t.endsWith(":") || NOMES_DE_GRUPO.test(t);
}

/** Hash curto e estável (djb2) do texto normalizado — vira nome de campo. */
export function chaveConduta(texto: string): string {
  const base = normalizar(texto).replace(/[^a-z0-9]/g, "");
  let h = 5381;
  for (let i = 0; i < base.length; i++) h = ((h << 5) + h + base.charCodeAt(i)) | 0;
  return "c" + (h >>> 0).toString(16).padStart(8, "0");
}

/**
 * Extrai os grupos/itens da seção "RECOMENDAÇÕES". Aceita o texto cru do
 * modelo ("1. 🟢 **X**", "- 🟡 Y", sub-itens "  - z") e o texto copiado da
 * tela ("1." numa linha, item na seguinte, "•" soltos).
 */
export function extrairCondutas(textoAnalise: string): GrupoConduta[] {
  const linhas = (textoAnalise || "").split("\n").map((l) => l.replace(/\s+$/, ""));
  const inicio = linhas.findIndex(ehInicioRecomendacoes);
  if (inicio < 0) return [];
  let fim = linhas.length;
  for (let i = inicio + 1; i < linhas.length; i++) {
    if (ehFimRecomendacoes(linhas[i])) {
      fim = i;
      break;
    }
  }

  const grupos: GrupoConduta[] = [];
  // Cursor mutável (objeto, não `let`): as funções abaixo o alteram e o TS
  // não estreitaria `let` corretamente através de closures.
  const cur: { grupo: GrupoConduta | null; item: ItemConduta | null } = {
    grupo: null,
    item: null,
  };
  let numeroPendente = false; // "1." sozinho numa linha (texto copiado da tela)
  let marcadorPendente = false; // "•" sozinho numa linha

  const abrirGrupo = (titulo: string) => {
    cur.grupo = { titulo, itens: [] };
    grupos.push(cur.grupo);
    cur.item = null;
  };
  const abrirItem = (l: string) => {
    if (!cur.grupo) abrirGrupo("Recomendações");
    cur.item = {
      chave: "",
      grupo: cur.grupo!.titulo,
      sinal: sinalDe(l),
      texto: limparMarcadores(l),
      detalhes: [],
    };
    cur.grupo!.itens.push(cur.item);
  };

  for (let i = inicio + 1; i < fim; i++) {
    const l = linhas[i];
    const t = l.trim();
    if (!t) continue;

    if (/^\d{1,2}[.)]$/.test(t)) {
      numeroPendente = true;
      continue;
    }
    if (/^[-•*]$/.test(t)) {
      marcadorPendente = true;
      continue;
    }
    if (numeroPendente) {
      numeroPendente = false;
      abrirItem(t);
      continue;
    }

    if (ehSubtitulo(l)) {
      marcadorPendente = false;
      abrirGrupo(
        t.replace(/^#+\s*/, "").replace(/\*\*/g, "").replace(/:$/, "").trim()
      );
      continue;
    }

    const temEmoji = EMOJIS.test(t);
    const ehNumerado = /^\d{1,2}[.)]\s+\S/.test(t);
    const ehBullet = /^[-•*]\s+\S/.test(t) || marcadorPendente;
    marcadorPendente = false;

    if (temEmoji || ehNumerado) {
      abrirItem(t);
    } else if (ehBullet) {
      // bullet sem emoji: sub-item do item aberto (ou item próprio se não há)
      if (cur.item) cur.item.detalhes.push(limparMarcadores(t));
      else abrirItem(t);
    } else if (cur.item) {
      cur.item.detalhes.push(limparMarcadores(t)); // continuação em prosa
    }
  }

  // chaves estáveis, únicas dentro da análise
  const vistas = new Set<string>();
  for (const g of grupos) {
    for (const it of g.itens) {
      let k = chaveConduta(it.texto);
      let n = 1;
      while (vistas.has(k)) k = chaveConduta(it.texto + "#" + n++);
      vistas.add(k);
      it.chave = k;
    }
  }
  return grupos.filter((g) => g.itens.length > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Dica "provável já prescrito" — cruzamento conservador, só com o que foi
// gerado DEPOIS da análise. Nunca decide: o quadro mostra a dica e o médico marca.
// ─────────────────────────────────────────────────────────────────────────────

export interface FonteReceita {
  data: string; // ISO
  medicamentos: string[]; // "Carvedilol 6,25 mg", "UDCA 300mg"...
}
export interface FontePedido {
  data: string; // ISO
  exames: string[]; // nomes do catálogo
}

const STOP = new Set([
  "de", "do", "da", "dos", "das", "com", "sem", "para", "por", "em", "e", "ou",
  "exame", "dosagem", "pesquisa", "total", "soro", "sangue", "serico", "serica",
  "mg", "ml", "comprimido", "comprimidos", "capsula", "capsulas", "gotas", "uso", "via",
]);

const tokens = (s: string): string[] =>
  normalizar(s)
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 3 && !STOP.has(t));

const fmtData = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** Primeiro token "nome" do medicamento (≥4 letras) — "Carvedilol 6,25 mg" → "carvedilol". */
function nomeMedicamento(m: string): string | null {
  const t = tokens(m).find((x) => /^[a-z]{4,}$/.test(x));
  return t || null;
}

export function detectarAcatamentoProvavel(
  item: ItemConduta,
  fontes: { receitas: FonteReceita[]; pedidos: FontePedido[] },
  dataAnalise: string
): string | null {
  const texto = normalizar([item.texto, ...item.detalhes].join(" "));
  const corte = new Date(dataAnalise).getTime();
  const depois = (iso: string) => {
    const t = new Date(iso).getTime();
    return !isNaN(t) && (isNaN(corte) || t >= corte);
  };

  for (const r of fontes.receitas) {
    if (!depois(r.data)) continue;
    for (const m of r.medicamentos) {
      const nome = nomeMedicamento(m);
      if (nome && new RegExp(`\\b${nome}`).test(texto)) {
        return `receita de ${fmtData(r.data)} (${m.trim()})`;
      }
    }
  }
  for (const p of fontes.pedidos) {
    if (!depois(p.data)) continue;
    for (const ex of p.exames) {
      const ts = tokens(ex);
      if (ts.length === 0) continue;
      // Sigla curta (TSH, AFP, INR): só como palavra inteira, para não casar
      // dentro de outra ("inr" em "intrínseco"). Nomes longos: todos os tokens.
      const todos = ts.every((t) =>
        t.length >= 4 ? texto.includes(t) : new RegExp(`\\b${t}\\b`).test(texto)
      );
      if (todos) return `pedido de exames de ${fmtData(p.data)} (${ex.trim()})`;
    }
  }
  return null;
}

export function resumirCondutas(
  grupos: GrupoConduta[],
  estados: Record<string, EstadoConduta> | undefined
): { total: number; pendentes: number; acatadas: number; descartadas: number } {
  let total = 0,
    acatadas = 0,
    descartadas = 0;
  for (const g of grupos) {
    for (const it of g.itens) {
      total++;
      const e = estados?.[it.chave];
      if (e?.status === "acatada") acatadas++;
      else if (e?.status === "descartada") descartadas++;
    }
  }
  return { total, pendentes: total - acatadas - descartadas, acatadas, descartadas };
}
